import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Boxes } from 'lucide-react';
import { auth as authApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@/components/ui/link';

// Mirrors the @Size(min, max) constraint on ResetPasswordRequest.newPassword in the backend
// (same rule as ChangePasswordRequest/RegisterRequest).
const PASSWORD_MIN_LENGTH = 10;
const PASSWORD_MAX_LENGTH = 128;

const successBannerClass =
  'rounded-control bg-[var(--color-success-bg)] px-3 py-2 text-sm text-[var(--color-success)]';

interface ResetPasswordFormProps {
  /** The raw token read from the `?token=` query param -- never shown to or typed by the user. */
  token: string;
}

function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const resetPassword = useMutation({
    // The backend never distinguishes *why* a token was rejected (invalid, expired,
    // already used) -- surfacing one generic message for any non-2xx keeps that
    // property intact on the client instead of trying to infer a more specific reason.
    mutationFn: (body: { token: string; newPassword: string }) => authApi.resetPassword(body),
    onError: (err) => {
      setFormError(getApiErrorMessage(err, t('auth.resetPasswordGenericError')));
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Guards against a submit event reaching the handler while a request is already
    // in flight, mirroring the equivalent guard on ProfilePage's change-password form.
    if (resetPassword.isPending) return;
    setFormError(null);
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setFormError(t('profile.passwordTooShort', { min: PASSWORD_MIN_LENGTH }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError(t('profile.passwordMismatch'));
      return;
    }
    resetPassword.mutate({ token, newPassword });
  };

  if (resetPassword.isSuccess) {
    return (
      <div className="space-y-4">
        <div role="status" aria-live="polite" className={successBannerClass}>
          {t('auth.resetPasswordSuccess')}
        </div>
        <Link to="/login" intent="inline" className="font-medium">
          {t('auth.backToLogin')}
        </Link>
      </div>
    );
  }

  const disabled = !newPassword.trim() || !confirmPassword.trim() || resetPassword.isPending;

  return (
    <>
      <form onSubmit={onSubmit} className="space-y-4" aria-label="reset-password-form">
        <div className="space-y-2">
          <Label htmlFor="newPassword">{t('auth.resetPasswordNewPassword')}</Label>
          <Input
            id="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            maxLength={PASSWORD_MAX_LENGTH}
            required
          />
          <p className="text-sm text-muted-foreground">
            {t('profile.newPasswordHint', { min: PASSWORD_MIN_LENGTH })}
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmNewPassword">{t('auth.resetPasswordConfirmPassword')}</Label>
          <Input
            id="confirmNewPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            maxLength={PASSWORD_MAX_LENGTH}
            required
          />
        </div>
        {formError && <div role="alert" className="text-sm text-destructive">{formError}</div>}
        <Button type="submit" className="w-full" loading={resetPassword.isPending} disabled={disabled}>
          {t('auth.resetPasswordSubmit')}
        </Button>
      </form>
      <p className="mt-4 text-center text-sm text-muted-foreground">
        <Link to="/login" intent="inline" className="font-medium">
          {t('auth.backToLogin')}
        </Link>
      </p>
    </>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Boxes className="h-6 w-6" />
          </div>
          <CardTitle>{t('auth.resetPasswordTitle')}</CardTitle>
          {token && <CardDescription>{t('auth.resetPasswordDesc')}</CardDescription>}
        </CardHeader>
        <CardContent>
          {token ? (
            <ResetPasswordForm token={token} />
          ) : (
            <div className="space-y-4">
              <div role="alert" className="text-sm text-destructive">
                <p className="font-medium">{t('auth.resetPasswordInvalidLinkTitle')}</p>
                <p className="mt-1 text-muted-foreground">{t('auth.resetPasswordInvalidLinkDesc')}</p>
              </div>
              <Link to="/forgot-password" intent="inline" className="font-medium">
                {t('auth.resetPasswordRequestNewLink')}
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
